/**
 * O fluxo de conclusão de uma Tarefa: o modal do Resultado e o da sugestão de próximo passo
 * (4.4 + 4.5 + 4.6, FR-17, FR-21, FR-22).
 *
 * **Ele nasceu na 4.6 por extração, e sem uma linha de comportamento novo.** Até a 4.5 os dois
 * diálogos moravam dentro de `TarefasDoNegocio.tsx`, porque havia **um** lugar que concluía Tarefa.
 * Depois desta história há **três** — o cartão da ficha, a tela de Tarefas e a fila do dia —, e
 * copiar duzentas linhas de estado e dois diálogos por lugar é garantir que a próxima correção
 * conserte um dos três. A suíte da 4.4/4.5 exercita este fluxo **pela tela**, e por isso ela é a
 * rede de segurança da extração: se ela precisar ser afrouxada para passar, a extração mudou
 * comportamento e está errada.
 *
 * **O que muda entre os três lugares é uma coisa só: para onde vai o aceite da sugestão.** Na
 * ficha, aceitar pré-preenche o formulário que já está no cartão; na tela de Tarefas e na fila do
 * dia, ele abre um `Dialogo` "Nova tarefa" pré-preenchido. É exatamente essa a forma de um retorno
 * de chamada, e é por isso que o destino é **injetado** (`aoAceitarASugestao`) em vez de escrito
 * aqui dentro — foi a pendência que a 4.5 deixou registrada para esta história.
 *
 * **Nenhuma Tarefa nasce daqui** (FR-22). Aceitar a sugestão não manda `POST` nenhum: ele entrega
 * o Tipo e o prazo a quem chamou, e a criação continua sendo o submit de um formulário com uma
 * pessoa tendo digitado o título. Um "Agendar sugestão" que escrevesse no serviço seria o sistema
 * criando a Tarefa — com uma confirmação por cima, mas criando.
 *
 * **A assimetria dos dois botões da conclusão atravessa intacta.** O primário ("Concluir e sugerir
 * próximo passo") conclui **e** abre a sugestão; o secundário ("Concluir sem resultado") termina
 * ali. O rótulo é o contrato com quem clica, e entregar a pergunta seguinte a quem escolheu o botão
 * que não a pede transformaria o convite do FR-21 em pedágio.
 *
 * **Os dois diálogos nunca estão na árvore ao mesmo tempo, e são duas linhas que sustentam isso.**
 * `aoTerminar` fecha a conclusão no mesmo lote em que a sugestão nasce, e o ajuste de render logo
 * abaixo fecha a sugestão quando uma conclusão nova abre. A segunda não é simetria decorativa: o
 * `Dialogo` não prende o foco (DW-180), então `Tab` alcança os botões "Concluir" das linhas atrás
 * do modal de sugestão, e sem ela um `Enter` ali montaria os dois ao mesmo tempo — dois
 * `role="dialog"` na mesma árvore, que é o oposto do que a suíte inteira assume com
 * `getByRole('dialog')` no singular.
 *
 * **As puras moram em `regras-de-tarefa.ts`, e são importadas de lá.** Elas não são deste fluxo:
 * `recusaDoDesfecho`, `corpoDaConclusao` e `tituloDaConclusao` já eram exportadas e já tinham suíte
 * própria. Elas saíram de `TarefasDoNegocio.tsx` no mesmo commit desta extração, para um módulo que
 * não desenha nada: deixá-las lá fecharia um **ciclo de import** (o cartão importa este componente,
 * e este importaria o cartão de volta) e arrastaria o cartão inteiro — e o `tarefas-do-negocio.css`
 * junto — para o bundle de `/tarefas` e de `/hoje`.
 *
 * **As duas folhas de estilo vêm explícitas**, e não de carona no módulo que importa este: o corpo
 * dos diálogos escreve classes de `tarefas-do-negocio.css` (os chips e o textarea) e de
 * `ficha-do-negocio.css` (a recusa, a ajuda e o rótulo). Deixá-las implícitas faria os diálogos
 * chegarem sem estilo no dia em que a tela que os monta for para outro chunk.
 */

import { useState } from 'react'

import { Dialogo } from './Dialogo'
import {
  AGENDAR_SUGESTAO,
  CONCLUIR_E_SUGERIR,
  CONCLUIR_SEM_RESULTADO,
  DEIXAR_SEM_TAREFA,
  DESCREVA_O_DESFECHO,
  PROXIMO_PASSO_VOCE_DECIDE,
  RESULTADO_E_OPCIONAL,
  RESULTADO_OUTROS,
  ROTULO_DO_RESULTADO,
  SEM_RESULTADOS_DO_TIPO,
  SUGESTAO_NAO_OBRIGA,
  TAMANHO_DO_RESULTADO,
  TITULO_DO_PROXIMO_PASSO,
  sugestaoDoProximoPasso,
} from './apresentacao'
import {
  CARREGANDO_OS_TIPOS,
  type EstadoDoCatalogo,
  RESULTADO_LIVRE,
  TIPOS_INDISPONIVEIS,
  corpoDaConclusao,
  recusaDoDesfecho,
  tituloDaConclusao,
} from './regras-de-tarefa'
import {
  type SugestaoDeProximoPasso,
  type Tarefa,
  type TarefaConcluida,
  useConcluirTarefa,
  useTiposDeTarefa,
} from './api'
import './ficha-do-negocio.css'
import './tarefas-do-negocio.css'

/**
 * O `id` do aviso sobre a lista de Resultados — e ele **é referenciado**, por `aria-describedby`
 * do campo de texto livre.
 *
 * O `<textarea>` não tem `id` próprio: nada aponta para ele, e quem o nomeia para o leitor de tela
 * é o `aria-label`. Um `id` sem referência é decoração que parece contrato — quem viesse depois
 * suporia um `<label htmlFor>` em algum lugar.
 */
const ID_DO_AVISO_DOS_RESULTADOS = 'tarefa-aviso-dos-resultados'

export interface PropriedadesDaConclusaoDeTarefa {
  /**
   * A Tarefa cujo modal de conclusão está aberto — uma de cada vez, ou `null`.
   *
   * É a **Tarefa**, e não o `id`: o modal precisa do `tipo_nome` para o título e do
   * `tipo_de_tarefa_id` para achar os Resultados daquele Tipo, e guardar só o `id` obrigaria a
   * procurar a linha na lista a cada render — numa lista que a mutação acabou de invalidar.
   *
   * Ela é **prop**, e não estado daqui, porque quem a abre é a tela: é lá que está o botão
   * "Concluir" de cada linha, e é lá que mora o gatilho a quem o foco volta.
   */
  concluindo: Tarefa | null
  /**
   * Fecha o modal de conclusão — sem concluir, ou depois de ter concluído.
   *
   * É a **mesma** saída de `Esc`, do clique no fundo e do sucesso dos dois botões, e por isso ela
   * é uma só: três portas com efeitos diferentes fariam a tela decidir por quem saiu pela errada.
   * Quem devolve o foco ao gatilho é quem implementa esta função — o elemento é de lá.
   */
  aoFechar: () => void
  /**
   * A conclusão saiu com `200`. Quem chama decide o que dizer — a confirmação com o título, no
   * molde do cartão da ficha, ou nada.
   *
   * Ela recebe a **Tarefa**, e não o título: um consumidor pode querer o Tipo dela ou o vínculo, e
   * passar só o texto obrigaria o próximo a mudar a assinatura.
   *
   * **Opcional, como `aoRecusarASugestao`**, e por uma razão de produto: nas duas telas em que a
   * linha concluída **sai** da lista (a de Tarefas pede pendentes, e a fila do dia traz só as com
   * prazo até hoje), o desaparecimento dela é a confirmação — melhor que uma frase. Quem precisa
   * dizer alguma coisa é o cartão da ficha, onde a lista mostra as duas metades e a linha ficaria
   * lá, igual. Obrigatória, ela se satisfazia com função vazia em dois dos três consumidores, e uma
   * função vazia é ruído que parece contrato.
   */
  aoConcluir?: ((tarefa: Tarefa) => void) | undefined
  /**
   * **O destino injetado** — a pendência que a 4.5 deixou para a 4.6.
   *
   * Recebe a Tarefa que acabou de terminar e o prazo sugerido (hoje + os dias da constante), e faz
   * com eles o que a tela souber fazer: pré-preencher o formulário que já está no cartão, ou abrir
   * um `Dialogo` de criação. Nenhuma requisição sai daqui.
   *
   * O prazo vem **pronto** em vez de a tela o recalcular: `prazoSugerido` é a mesma pura que a
   * frase do modal usa para dizer "em três dias", e recalculá-la do outro lado abriria a porta
   * para a frase e a data discordarem.
   */
  aoAceitarASugestao: (tarefa: Tarefa, sugestao: SugestaoDeProximoPasso) => void
  /**
   * A sugestão foi recusada — por "Deixar sem tarefa", `Esc` ou clique no fundo.
   *
   * Ela existe para o **foco**, e não para o dado: nada é agendado, nada é perguntado, e nenhuma
   * requisição sai. O botão que abriu a conclusão já não existe (a linha foi reescrita), então
   * quem sabe para onde mandar o foco é a tela. Opcional: uma tela sem alvo melhor pode deixá-la
   * de fora, e o foco cai no `body` como cairia de qualquer forma.
   */
  aoRecusarASugestao?: (() => void) | undefined
}

/**
 * Os dois diálogos do fluxo, montados lado a lado — nunca os dois na árvore ao mesmo tempo.
 *
 * Ele monta o modal de conclusão com uma `key` na Tarefa, e essa `key` é o reset: escolha, texto
 * livre, recusa local e o estado da mutação nascem limpos a cada Tarefa nova, sem um `useEffect`
 * que os zerasse depois do render nem um `reset()` chamado de dentro dele. É o mecanismo que a
 * própria documentação do React indica para "recomeçar quando a identidade muda", e ele substitui,
 * uma a uma, as cinco linhas que `abrirAConclusao` executava na 4.5.
 */
export function ConclusaoDeTarefa({
  concluindo,
  aoFechar,
  aoConcluir,
  aoAceitarASugestao,
  aoRecusarASugestao,
}: PropriedadesDaConclusaoDeTarefa) {
  /**
   * A Tarefa cuja conclusão acabou de sair com `200` e sobre a qual o modal de próximo passo
   * pergunta — ou `null`, que é o estado da tela em todo o resto do tempo (4.5, FR-21).
   *
   * É a **Tarefa**, e não o `id`: a sugestão precisa do `tipo_nome` para a frase e do
   * `tipo_de_tarefa_id` para o destino, e a linha da lista acabou de ser invalidada — procurá-la
   * ali a cada render é procurar o que pode não estar mais lá (com um recorte ligado, a Tarefa
   * concluída sai da lista).
   *
   * **Ela vive aqui, e fora da `key` do modal de conclusão**, porque ela nasce no instante em que
   * aquele modal fecha: dentro dele, ela morreria junto.
   */
  //: A Tarefa concluída **com** sugestão — a conclusão pode vir com `sugestao: null` (11.6: nem o
  //: próprio Tipo da Tarefa está ativo), e aí não há convite a abrir.
  const [sugerindo, setSugerindo] = useState<
    (TarefaConcluida & { sugestao: SugestaoDeProximoPasso }) | null
  >(null)

  /*
   * O ajuste de render que fecha a sugestão quando uma conclusão nova abre — a linha que a 4.5
   * escrevia dentro de `abrirAConclusao`.
   *
   * Ele é comparação durante o render, e não um `useEffect`: o efeito rodaria **depois** de a
   * árvore já ter sido pintada com os dois diálogos, e o quadro intermediário é exatamente o
   * estado que a docstring do módulo diz não existir. É o padrão que o React documenta para
   * "ajustar estado quando uma prop muda", e o `setState` durante o render de si mesmo é o caso
   * que ele suporta.
   *
   * A guarda `!== null` é o que preserva a sugestão no fechamento: quando a conclusão termina bem,
   * `concluindo` vira `null` e `sugerindo` nasce **no mesmo lote** — zerar aqui apagaria o modal
   * que acabou de ser pedido.
   */
  const [ultimaEmConclusao, setUltimaEmConclusao] = useState(concluindo)
  if (concluindo !== ultimaEmConclusao) {
    setUltimaEmConclusao(concluindo)
    if (concluindo !== null) {
      setSugerindo(null)
    }
  }

  function terminar(tarefa: TarefaConcluida, comResultado: boolean) {
    aoConcluir?.(tarefa)
    aoFechar()
    //: **A sugestão é do primário, e só dele** (4.5, FR-21, protótipo `:1338`). O rótulo é o
    //: contrato com quem clica: "Concluir sem resultado" promete terminar, e entregar a pergunta
    //: seguinte a quem escolheu o botão que não a pede transformaria o convite do FR-21 em pedágio.
    const { sugestao } = tarefa
    if (comResultado && sugestao !== null) {
      setSugerindo({ ...tarefa, sugestao })
    }
  }

  function recusarASugestao() {
    setSugerindo(null)
    aoRecusarASugestao?.()
  }

  function aceitarASugestao() {
    if (sugerindo === null) {
      return
    }
    setSugerindo(null)
    //: 11.6 (FR-21): a sugestão é a do **serviço** — a configurada para o Resultado escolhido,
    //: ou a regra padrão (mesmo tipo, 3 dias). O prazo vem pronto; nada é criado aqui (FR-22).
    aoAceitarASugestao(sugerindo, sugerindo.sugestao)
  }

  return (
    <>
      {/* Montado **só quando há Tarefa em conclusão**, e não sempre com um `null` por dentro: as
          consultas dele (o catálogo de Tipos) e a mutação são hooks, e hooks montados custam uma
          ida à rede em toda tela que monta este componente — inclusive a tela Hoje, que não abre
          modal nenhum na maioria das visitas.

          A `key` continua sendo o reset: escolha, texto livre, recusa local e o estado da mutação
          nascem limpos a cada Tarefa nova, sem um `useEffect` que os zerasse depois do render nem
          um `reset()` chamado de dentro dele. */}
      {concluindo === null ? null : (
        <DialogoDaConclusao
          key={concluindo.id}
          tarefa={concluindo}
          aoFechar={aoFechar}
          aoTerminar={terminar}
        />
      )}

      {/* O segundo modal do UX-DR6 (4.5, FR-21, protótipo `:1332`).

          **Ele é o mesmo `Dialogo`**, com `acaoSecundaria` — a prop que a 4.4 acrescentou. Um
          rodapé desenhado à mão aqui teria de repetir o foco que entra, o `Esc`, o `aria-modal` e
          o clique no fundo, que é justamente o que não pode divergir.

          `ocupado={false}` e `erro={undefined}` porque não há requisição nenhuma neste modal: as
          duas saídas são locais (FR-22), e um estado "ocupado" aqui prometeria uma escrita que não
          existe. */}
      {sugerindo === null ? null : (
        <Dialogo
          titulo={TITULO_DO_PROXIMO_PASSO}
          subtitulo={PROXIMO_PASSO_VOCE_DECIDE}
          rotuloDoConfirmar={AGENDAR_SUGESTAO}
          /* "Deixar sem tarefa" no lugar do "Cancelar": não há o que cancelar — a conclusão já
             aconteceu, e o que se recusa é a sugestão. É a **mesma** função de `aoFechar`, que é o
             que faz o botão, o `Esc` e o clique no fundo serem uma saída só. */
          acaoSecundaria={{ rotulo: DEIXAR_SEM_TAREFA, aoAcionar: recusarASugestao }}
          ocupado={false}
          erro={undefined}
          aoFechar={recusarASugestao}
          aoConfirmar={aceitarASugestao}
        >
          <div className="tarefas__sugestao">
            {/* A frase é a do protótipo, montada pela pura: o número dos dias sai da mesma
                constante que `prazoSugerido` soma, e por isso a tela não tem como dizer "em 3
                dias" e preencher outra data. O Tipo vem de `tipo_nome`, congelado no registro
                (FR-69) — uma Tarefa de Tipo excluído continua se nomeando aqui. */}
            <span className="tarefas__sugestao-frase">
              {sugestaoDoProximoPasso(
                sugerindo.sugestao.tipo_nome,
                sugerindo.sugestao.prazo_em_dias,
              )}
            </span>
            <span className="tarefas__sugestao-apoio">{SUGESTAO_NAO_OBRIGA}</span>
          </div>
        </Dialogo>
      )}
    </>
  )
}

interface PropriedadesDoDialogoDaConclusao {
  tarefa: Tarefa
  aoFechar: () => void
  aoTerminar: (tarefa: TarefaConcluida, comResultado: boolean) => void
}

/**
 * O modal do Resultado (UX-DR6, FR-17) — e o dono de todo o estado que morre com ele.
 *
 * Ele é componente próprio, e não um bloco dentro do de cima, por duas razões que se somam. A
 * primeira é a `key`: escolha, texto livre, recusa local e a **mutação** precisam recomeçar a cada
 * Tarefa nova, e remontar é o único jeito de zerar os quatro de uma vez sem um `reset()` chamado no
 * meio de um render. A segunda é o **custo**: os hooks dele — o catálogo de Tipos e a mutação —
 * só existem enquanto o modal está aberto, e não em toda tela que monta o fluxo. Escritos no
 * componente de fora, eles disparariam um `GET /tipos-de-tarefa` na abertura da tela Hoje, que na
 * maioria das visitas não conclui nada.
 */
function DialogoDaConclusao({ tarefa, aoFechar, aoTerminar }: PropriedadesDoDialogoDaConclusao) {
  const catalogo = useTiposDeTarefa()
  const conclusao = useConcluirTarefa()

  /** O chip escolhido: o `id` de um Resultado, `RESULTADO_LIVRE`, ou `null` — que é o padrão. */
  const [escolha, setEscolha] = useState<string | null>(null)
  const [desfecho, setDesfecho] = useState('')
  const [recusaDaConclusao, setRecusaDaConclusao] = useState<string | null>(null)

  /*
   * O estado do catálogo, reduzido ao que o aviso precisa saber. `isLoading` é o primeiro ramo
   * porque ele é o único que muda sozinho: sem rede a consulta fica **pausada** — nem erro, nem
   * `isLoading` —, e ali o catálogo é tratado como pronto e vazio, que é o que a tela mostra.
   */
  const estadoDoCatalogo: EstadoDoCatalogo = catalogo.isError
    ? 'indisponivel'
    : catalogo.isLoading
      ? 'carregando'
      : 'pronto'

  /**
   * Os Resultados daquele Tipo — do catálogo que a tela já leu, sem requisição nova.
   *
   * `GET /tipos-de-tarefa` traz os Resultados **dentro** do Tipo (4.1), e é a mesma chave de cache
   * que a Administração usa: abrir o modal não custa uma ida à rede.
   *
   * **Não há filtro por `ativo` aqui**: a rota já devolve só os que estão em circulação (FR-16), e
   * um segundo filtro na tela seria a segunda escrita da mesma regra. E o Tipo **excluído**
   * simplesmente não está no catálogo: uma Tarefa dele cai no `?? []` e continua podendo ser
   * concluída — que é exatamente o que o FR-69 promete para as Tarefas antigas.
   */
  const resultadosDoTipo =
    (catalogo.data ?? []).find((umTipo) => umTipo.id === tarefa.tipo_de_tarefa_id)?.resultados ?? []

  /**
   * O que o modal escreve no lugar dos chips quando não há nenhum — e são **três** frases, e não
   * uma.
   *
   * "Não há Resultado para escolher" tem três causas: o catálogo ainda não chegou, ele falhou, ou
   * ele chegou e aquele Tipo não tem opção nenhuma (FR-16). Só a terceira é
   * `SEM_RESULTADOS_DO_TIPO` — as outras duas afirmariam sobre o vocabulário daquele Tipo uma coisa
   * que ninguém sabe ainda, e mandariam a pessoa escrever à mão um desfecho que a lista talvez
   * tivesse.
   *
   * Nas três, concluir continua possível: nenhuma delas manda parar, porque registrar o Resultado
   * nunca foi obrigatório (FR-17). O que muda é o que a tela diz sobre a lista vazia.
   *
   * `null` quando há chips: aí não há nada a explicar.
   */
  const avisoDosResultados =
    resultadosDoTipo.length > 0
      ? null
      : estadoDoCatalogo === 'indisponivel'
        ? TIPOS_INDISPONIVEIS
        : estadoDoCatalogo === 'carregando'
          ? CARREGANDO_OS_TIPOS
          : SEM_RESULTADOS_DO_TIPO

  /**
   * Escolhe (ou **desmarca**) um chip de Resultado.
   *
   * O clique no chip já pressionado limpa a escolha, e isso é o que `aria-pressed` promete: o
   * atributo diz ao leitor de tela que aquilo é um botão de dois estados, e um controle que só
   * liga seria uma promessa quebrada — quem marcasse "Atendida" por engano não teria como voltar a
   * "sem Resultado" sem fechar o modal e reabrir.
   *
   * **É diferente do Motivo de perda da ficha**, e a diferença é o requisito: lá o Motivo é
   * **obrigatório** (o confirmar fica desabilitado sem ele), e desmarcar levaria a um estado que o
   * serviço recusa. Aqui o Resultado é opcional (FR-17), e "nenhum" é um desfecho legítimo — o
   * mesmo que o botão "Concluir sem resultado" manda.
   */
  function escolherOResultado(qual: string) {
    setRecusaDaConclusao(null)
    setEscolha((atual) => (atual === qual ? null : qual))
  }

  /**
   * Conclui a Tarefa do modal — com o Resultado escolhido, ou sem nenhum.
   *
   * **Os dois botões chamam esta função**, e o argumento diz **qual deles** foi clicado: o
   * secundário conclui limpando a escolha (é o "Concluir sem resultado" do protótipo), e o
   * primário conclui com ela — e é ele, e só ele, que abre a sugestão de próximo passo depois do
   * `200` (4.5, FR-21). O mesmo argumento carrega as duas coisas porque elas são a mesma decisão:
   * o rótulo do primário promete o Resultado **e** a pergunta seguinte, e o do secundário promete
   * terminar. Dois parâmetros separados permitiriam a combinação que nenhum botão oferece.
   *
   * **A sugestão vive no `onSuccess`**, e não ao lado da chamada: conclusão recusada (`409`, `403`,
   * rede) mantém o modal de conclusão aberto com a recusa do serviço, e nada é sugerido — sugerir
   * o próximo passo de uma Tarefa que não terminou seria a tela contando duas histórias ao mesmo
   * tempo.
   *
   * A recusa local só é conferida no caminho **com** Resultado: concluir sem desfecho não tem o que
   * recusar, e rodar a guarda ali faria o botão que existe para escapar do texto livre reclamar
   * justamente do texto livre.
   */
  function concluir(comResultado: boolean) {
    const rascunho = comResultado ? { escolha, texto: desfecho } : { escolha: null, texto: '' }
    const local = comResultado ? recusaDoDesfecho(rascunho) : null
    setRecusaDaConclusao(local)
    if (local !== null) {
      return
    }
    //: A Tarefa é lida **antes** do envio e guardada aqui: quem chama zera `concluindo`, e tanto a
    //: confirmação quanto a sugestão precisam saber **qual** Tarefa terminou — com um recorte
    //: ligado ela é a única coisa que sobra na tela sobre aquela linha.
    conclusao.mutate(
      { tarefaId: tarefa.id, conclusao: corpoDaConclusao(rascunho) },
      {
        onSuccess: (concluida) => {
          aoTerminar(concluida, comResultado)
        },
      },
    )
  }

  return (
    <Dialogo
      titulo={tituloDaConclusao(tarefa.tipo_nome)}
      subtitulo={RESULTADO_E_OPCIONAL}
      rotuloDoConfirmar={CONCLUIR_E_SUGERIR}
      /* Os **dois** botões concluem, e por isso o secundário substitui o "Cancelar": quem
         abriu o modal já decidiu concluir, e a saída sem concluir continua existindo por `Esc`
         e pelo clique no fundo. O que os separa é o que vem **depois** do `200`: o primário
         abre o modal de próximo passo (4.5), e o secundário termina ali. */
      acaoSecundaria={{
        rotulo: CONCLUIR_SEM_RESULTADO,
        aoAcionar: () => {
          concluir(false)
        },
      }}
      ocupado={conclusao.isPending}
      erro={conclusao.isError ? conclusao.error : undefined}
      aoFechar={aoFechar}
      aoConfirmar={() => {
        concluir(true)
      }}
    >
      <div className="tarefas__resultados" role="group" aria-label={ROTULO_DO_RESULTADO}>
        <span className="ficha__dialogo-rotulo">{ROTULO_DO_RESULTADO}</span>
        {/* Estado real, e não defensivo: o Admin remove Resultados por `ativo = false` (FR-16),
            e um Tipo pode ficar sem nenhum — e o Tipo **excluído** nem está no catálogo. A
            frase não manda parar: concluir continua possível, com "Outros" ou sem nada. */}
        {avisoDosResultados === null ? null : (
          <p className="ficha__ajuda" id={ID_DO_AVISO_DOS_RESULTADOS}>
            {avisoDosResultados}
          </p>
        )}
        <div className="ficha__chips">
          {resultadosDoTipo.map((resultado) => (
            <button
              key={resultado.id}
              type="button"
              className="ficha__chip"
              aria-pressed={escolha === resultado.id}
              onClick={() => {
                escolherOResultado(resultado.id)
              }}
            >
              {resultado.nome}
            </button>
          ))}
          {/* "Outros" é o último, e é opção da **tela**: ele não tem linha no catálogo (FR-17),
              e o que ele abre é o campo de texto livre. */}
          <button
            type="button"
            className="ficha__chip"
            aria-pressed={escolha === RESULTADO_LIVRE}
            onClick={() => {
              escolherOResultado(RESULTADO_LIVRE)
            }}
          >
            {RESULTADO_OUTROS}
          </button>
        </div>
        {escolha === RESULTADO_LIVRE ? (
          /* `aria-label`, e não um `<label>` visível: o protótipo mostra só a caixa com o texto
             de apoio dentro (`:741`), e um rótulo repetido acima dela diria duas vezes a mesma
             coisa. O `placeholder` sozinho não seria nome acessível — ele some ao digitar.

             `aria-describedby` liga a caixa à frase que explica **por que** ela é o único
             caminho quando não há chip nenhum ("este tipo não tem resultados cadastrados"):
             sem o vínculo, quem navega por teclado chega ao campo sem ter passado pelo aviso, e
             a caixa aparece do nada. Ele só existe quando o aviso existe — um `aria-describedby`
             apontando para um id ausente é pior que nenhum. */
          <textarea
            className="ficha__entrada tarefas__desfecho"
            aria-label={DESCREVA_O_DESFECHO}
            aria-describedby={avisoDosResultados === null ? undefined : ID_DO_AVISO_DOS_RESULTADOS}
            placeholder={DESCREVA_O_DESFECHO}
            maxLength={TAMANHO_DO_RESULTADO}
            value={desfecho}
            onChange={(evento) => {
              setRecusaDaConclusao(null)
              setDesfecho(evento.target.value)
            }}
          />
        ) : null}
        {/* A recusa local vem **antes** da do serviço, que o `Dialogo` desenha logo abaixo:
            quando ela existe, a ida à rede nem aconteceu. */}
        {recusaDaConclusao !== null ? (
          <p className="ficha__erro" role="alert">
            {recusaDaConclusao}
          </p>
        ) : null}
      </div>
    </Dialogo>
  )
}
